import { localDB } from './pouchdb-config';
import cuid from 'cuid';
import { countryCurrencyMap, currencyCountryMap } from './init-data';

const adminProductService = {
  getAllProducts: async (includeInactive = true) => {
    try {
      console.log('Fetching all products for admin view...');
      console.log('Include inactive products:', includeInactive);
      
      // First try to get all docs to see what's in the database
      const allDocs = await localDB.allDocs({ include_docs: true });
      console.log('All documents in database:', allDocs.rows.length);
      console.log('All docs:', allDocs.rows.map(row => ({ id: row.id, type: row.doc?.type })));
      
      // Use allDocs and filter for products
      const result = await localDB.allDocs({ include_docs: true });
      
      let products = result.rows
        ?.map(row => row.doc)
        ?.filter(doc => doc && doc.type === 'product') || [];
      console.log(`Found ${products.length} products using find query`);
      console.log('Products found:', products.map(p => ({ id: p._id, name: p.name, active: p.active })));
      
      // Filter out inactive products if needed
      if (!includeInactive) {
        products = products.filter(product => product.active !== false);
        console.log(`Filtered to ${products.length} active products`);
      }
      
      return products;
    } catch (error) {
      console.error('Error getting products:', error);
      return []; // Return empty array instead of throwing error
    }
  },

  getProductById: async (id) => {
    try {
      const product = await localDB.get(id);
      console.log(`Retrieved product ${id}:`, JSON.stringify(product, null, 2));
      return product;
    } catch (error) {
      console.error(`Error getting product with ID ${id}:`, error);
      throw error;
    }
  },

  addProduct: async (productData) => {
    try {
      const timestamp = new Date().toISOString();
      
      // Create product document with minimal required fields
      const product = {
        _id: cuid(),
        type: 'product',
        name: productData.name,
        description: productData.description || '',
        is_organic: productData.is_organic ? 1 : 0,
        image_url: productData.image_url || '',
        unit: productData.unit || 'kg',
        active: true, // New products are active by default
        created_at: timestamp,
        updated_at: timestamp
      };
      
      // Add pricing data directly
      if (productData.pricing) {
        product.pricing = productData.pricing;
      }
      
      // Add stock data directly
      if (productData.countryStock) {
        product.countryStock = productData.countryStock;
      }
      
      // Add availability
      product.availability = productData.availability || [];
      
      console.log('Adding product with data:', JSON.stringify(product, null, 2));
      const response = await localDB.put(product);
      console.log('Add product response:', response);
      
      // Fetch the added product to confirm
      const addedProduct = await localDB.get(product._id);
      console.log('Added product in DB:', addedProduct);
      
      return addedProduct;
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  },

  updateProduct: async (id, productData) => {
    try {
      // Get existing product with latest revision
      const existingProduct = await localDB.get(id);
      console.log('Existing product before update:', JSON.stringify(existingProduct, null, 2));
      
      // Create a completely new object with the updated data
      const updatedProduct = {
        _id: existingProduct._id,
        _rev: existingProduct._rev,
        type: 'product',
        name: productData.name || existingProduct.name,
        description: productData.description || existingProduct.description,
        is_organic: productData.is_organic !== undefined ? productData.is_organic : (existingProduct.is_organic || 0),
        image_url: productData.image_url !== undefined ? productData.image_url : (existingProduct.image_url || ''),
        unit: productData.unit || existingProduct.unit || 'kg',
        active: productData.active !== undefined ? productData.active : (existingProduct.active !== false),
        productHighlights: productData.productHighlights !== undefined ? productData.productHighlights : (existingProduct.productHighlights || ''),
        appearance: productData.appearance !== undefined ? productData.appearance : (existingProduct.appearance || ''),
        benefits: productData.benefits !== undefined ? productData.benefits : (existingProduct.benefits || ''),
        shelfLife: productData.shelfLife !== undefined ? productData.shelfLife : (existingProduct.shelfLife || ''),
        countryOfOrigin: productData.countryOfOrigin || existingProduct.countryOfOrigin || 'India',
        brandName: productData.brandName || existingProduct.brandName || 'Organic',
        disclaimer: productData.disclaimer !== undefined ? productData.disclaimer : (existingProduct.disclaimer || ''),
        videoUrl: productData.videoUrl !== undefined ? productData.videoUrl : (existingProduct.videoUrl || ''),
        category: productData.category !== undefined ? productData.category : existingProduct.category,
        cardBenefit: productData.cardBenefit !== undefined ? productData.cardBenefit : (existingProduct.cardBenefit || ''),
        cardPackageInfo: productData.cardPackageInfo !== undefined ? productData.cardPackageInfo : (existingProduct.cardPackageInfo || ''),
        cardKeyFeatures: productData.cardKeyFeatures !== undefined ? productData.cardKeyFeatures : (existingProduct.cardKeyFeatures || ''),
        highlights: productData.highlights !== undefined ? productData.highlights : (existingProduct.highlights || ''),
        ingredients: productData.ingredients !== undefined ? productData.ingredients : (existingProduct.ingredients || ''),
        usageInstructions: productData.usageInstructions !== undefined ? productData.usageInstructions : (existingProduct.usageInstructions || ''),
        storageInstructions: productData.storageInstructions !== undefined ? productData.storageInstructions : (existingProduct.storageInstructions || ''),
        certifications: productData.certifications !== undefined ? productData.certifications : (existingProduct.certifications || ''),
        galleryImages: productData.galleryImages !== undefined ? productData.galleryImages : (existingProduct.galleryImages || []),
        created_at: existingProduct.created_at,
        updated_at: new Date().toISOString()
      };
      
      // Add pricing data directly from productData
      updatedProduct.pricing = productData.pricing || {};
      
      // Add stock data directly from productData
      updatedProduct.countryStock = productData.countryStock || {};
      
      // Add availability
      updatedProduct.availability = productData.availability || existingProduct.availability || [];
      
      console.log('Updating product with data:', JSON.stringify(updatedProduct, null, 2));
      
      // Use put with complete object
      const response = await localDB.put(updatedProduct);
      console.log('Update response:', response);
      
      // Fetch the updated product to confirm
      const updatedDoc = await localDB.get(id);
      console.log('Updated product in DB:', JSON.stringify(updatedDoc, null, 2));
      
      return updatedDoc;
    } catch (error) {
      console.error(`Error updating product with ID ${id}:`, error);
      throw error;
    }
  },

  setProductActive: async (id, active) => {
    try {
      console.log(`Setting product ${id} active status to ${active}`);
      
      // Get existing product with latest revision
      const product = await localDB.get(id);
      console.log('Product before active status change:', JSON.stringify(product, null, 2));
      
      // Create a new object with the updated active status
      const updatedProduct = {
        ...product,
        active: active,
        updated_at: new Date().toISOString()
      };
      
      console.log('Updating product with active status:', JSON.stringify(updatedProduct, null, 2));
      
      // Use put with complete object
      const response = await localDB.put(updatedProduct);
      console.log('Update response:', response);
      
      // Fetch the updated product to confirm
      const updatedDoc = await localDB.get(id);
      console.log('Updated product in DB:', JSON.stringify(updatedDoc, null, 2));
      
      return updatedDoc;
    } catch (error) {
      console.error(`Error setting product active status with ID ${id}:`, error);
      throw error;
    }
  },

  deleteProduct: async (id) => {
    try {
      console.log(`Marking product ${id} as inactive`);
      
      // Instead of deleting, just mark as inactive
      const result = await adminProductService.setProductActive(id, false);
      console.log('Product marked as inactive:', JSON.stringify(result, null, 2));
      
      return result;
    } catch (error) {
      console.error(`Error marking product as inactive with ID ${id}:`, error);
      throw error;
    }
  },

  bulkCreateProducts: async (products) => {
    try {
      console.log('Bulk creating products:', products.length);
      
      const result = await localDB.bulkDocs(products);
      console.log('Bulk create result:', result);
      
      return result;
    } catch (error) {
      console.error('Error bulk creating products:', error);
      throw error;
    }
  }
};

export default adminProductService;